import { createSelector } from "@reduxjs/toolkit";
import { LENGTH_COMP_CLASSES } from "@multiconsult-group/multiplacer-core";
import type { IPixPath, IPixPolygon } from "../../../../../components/PixBimViewerV2/types";
import {
  openapi,
  type GetMcbimFloorsApiArg,
  type GetMcbimItemsInSpaceApiArg,
  type GetMcbimSpaceApiArg,
} from "../../../../../../openapi";
import { IRootState } from "../../../../../store";
import { selectProjectNo } from "../../../../projectSelectors";
import { ISSUE_STATES_OPTIONS } from "../../issueConstants";
import { selectFilteredIssuesOnBoard, selectIssuesWithAllEnrichedData } from "../../issueSelectors";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";
import {
  buildCeilingGridPaths,
  buildMcbimItemPolygons,
  type FloorplanMcbimItemWithGeometry,
  buildSpacePolygon,
  buildSpacePolygons,
  getIssueCountByRoomNumberForFloor,
  isIssueConnectedToRoomOnFloor,
} from "./floorplanUtils";
import { getInitialFloorplanRegisterState, type IFloorplanRegisterState, type IFloorplanState } from "./floorplanSlice";
import { selectMcbimSpacesQuery } from "./mcbimSelectors";

const DEFAULT_FLOORPLAN_REGISTER_STATE: IFloorplanRegisterState = getInitialFloorplanRegisterState("");

export const selectFloorplanState = (state: IRootState): IFloorplanState => state.floorplanReducer;
export const selectCurrentFloorplanRegisterState = createSelector(
  [selectFloorplanState, selectIssueBoardId],
  (floorplan, issueBoardId): IFloorplanRegisterState => {
    if (issueBoardId == null) return DEFAULT_FLOORPLAN_REGISTER_STATE;

    return floorplan.byIssueBoardId?.[issueBoardId] ?? DEFAULT_FLOORPLAN_REGISTER_STATE;
  },
);

export const selectSelectedFloor = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.selectedFloor,
);
export const selectSelectedFloorSpaceId = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.selectedFloorSpaceId,
);
export const selectSelectedFloorSpaceIds = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.selectedFloorSpaceIds,
);
export const selectClickedRoomNumberInModal = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.clickedRoomNumberInModal,
);
export const selectRoomSearchText = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.roomSearchText,
);
export const selectFloorSearchText = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.floorSearchText,
);
export const selectShowOnlyFloorsWithIssues = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.showOnlyFloorsWithIssues,
);
export const selectShowLengthItemsInSelectedRoom = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.showLengthItemsInSelectedRoom,
);
export const selectFloorplanRotation = createSelector(
  [selectCurrentFloorplanRegisterState],
  (floorplan) => floorplan.floorplanRotation,
);

const LENGTH_COMP_CLASS_SET = new Set<string>(LENGTH_COMP_CLASSES);

export const selectMcbimFloorsArgs = createSelector([selectProjectNo], (projectNo): GetMcbimFloorsApiArg | null => {
  if (projectNo == null) return null;

  return {
    projectNo,
  };
});

export const selectHttpMcbimFloors = createSelector(
  [(state: IRootState) => state, selectMcbimFloorsArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimFloors.select(args)(state);
    return q.data ?? [];
  },
);

export const selectMcbimSpacesOnSelectedFloorArgs = createSelector(
  [selectProjectNo, selectSelectedFloor],
  (projectNo, selectedFloor) => {
    if (projectNo == null || selectedFloor.trim() === "") return null;

    return {
      projectNo,
      floorName: selectedFloor,
    };
  },
);

export const selectHttpMcbimSpacesOnSelectedFloor = createSelector(
  [(state: IRootState) => state, selectMcbimSpacesOnSelectedFloorArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimSpacesOnFloor.select(args)(state);
    return q.data ?? [];
  },
);

export const selectSelectedFloorSpace = createSelector(
  [selectHttpMcbimSpacesOnSelectedFloor, selectSelectedFloorSpaceId],
  (spaces, selectedFloorSpaceId) => spaces.find((space) => space._id === selectedFloorSpaceId) ?? null,
);

export const selectMcbimItemsInSelectedSpaceArgs = createSelector(
  [selectProjectNo, selectSelectedFloorSpace],
  (projectNo, selectedSpace): GetMcbimItemsInSpaceApiArg | null => {
    if (projectNo == null || selectedSpace == null) return null;

    return {
      projectNo,
      spaceId: selectedSpace._id,
    };
  },
);

export const selectHttpMcbimItemsInSelectedSpace = createSelector(
  [(state: IRootState) => state, selectMcbimItemsInSelectedSpaceArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimItemsInSpace.select(args)(state);
    return q.data ?? [];
  },
);

export const selectMcbimSpaceInModalArgs = createSelector(
  [selectProjectNo, selectClickedRoomNumberInModal],
  (projectNo, clickedRoomNumberInModal): GetMcbimSpaceApiArg | null => {
    if (projectNo == null || clickedRoomNumberInModal == null) return null;

    return {
      projectNo,
      roomNumber: clickedRoomNumberInModal,
    };
  },
);

export const selectMcbimItemsInModalRoomArgs = createSelector(
  [selectProjectNo, selectClickedRoomNumberInModal],
  (projectNo, clickedRoomNumberInModal): GetMcbimItemsInSpaceApiArg | null => {
    if (projectNo == null || clickedRoomNumberInModal == null) return null;

    return {
      projectNo,
      spaceName: clickedRoomNumberInModal,
    };
  },
);

export const selectHttpMcbimSpaceInModal = createSelector(
  [(state: IRootState) => state, selectMcbimSpaceInModalArgs],
  (state, args) => {
    if (!args) return null;
    const q = openapi.endpoints.getMcbimSpace.select(args)(state);
    return q.data ?? null;
  },
);

export const selectHttpMcbimItemsInModalRoom = createSelector(
  [(state: IRootState) => state, selectMcbimItemsInModalRoomArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimItemsInSpace.select(args)(state);
    return q.data ?? [];
  },
);

export const selectVisibleMcbimItemsInSelectedSpace = createSelector(
  [selectHttpMcbimItemsInSelectedSpace, selectShowLengthItemsInSelectedRoom],
  (items, showLengthItemsInSelectedRoom): FloorplanMcbimItemWithGeometry[] => {
    if (showLengthItemsInSelectedRoom) return items;

    return items.filter((item) => !LENGTH_COMP_CLASS_SET.has(item.instanceClass ?? ""));
  },
);

export const selectVisibleMcbimItemsInModalRoom = createSelector(
  [selectHttpMcbimItemsInModalRoom, selectShowLengthItemsInSelectedRoom],
  (items, showLengthItemsInSelectedRoom): FloorplanMcbimItemWithGeometry[] => {
    if (showLengthItemsInSelectedRoom) return items;

    return items.filter((item) => !LENGTH_COMP_CLASS_SET.has(item.instanceClass ?? ""));
  },
);

export const selectConnectedBimSpaceForSelectedFloorSpace = createSelector(
  [selectMcbimSpacesQuery, selectSelectedFloorSpaceId],
  ({ data }, selectedFloorSpaceId) => data.find((space) => String(space.spaceDbId) === selectedFloorSpaceId) ?? null,
);

export const selectConnectedBimSpacesForSelectedFloorSpaces = createSelector(
  [selectMcbimSpacesQuery, selectSelectedFloorSpaceIds],
  ({ data }, selectedFloorSpaceIds) => {
    const selectedIds = new Set(selectedFloorSpaceIds);
    return data.filter((space) => selectedIds.has(String(space.spaceDbId)));
  },
);

export const selectIssuesForSelectedFloorSpace = createSelector(
  [selectFilteredIssuesOnBoard, selectSelectedFloorSpace, selectSelectedFloor],
  (issues, selectedFloorSpace, selectedFloor) => {
    const roomNumber = selectedFloorSpace?.name ?? "";

    if (roomNumber.trim() === "") {
      return [];
    }

    return issues
      .filter((issue) => isIssueConnectedToRoomOnFloor(issue, roomNumber, selectedFloor))
      .map((issue) => ({
        _id: issue._id,
        id: issue.id,
        status: issue.status,
        title: issue.title,
        statusColor: ISSUE_STATES_OPTIONS.find((item) => item.value === issue.status)?.color ?? "gray",
      }));
  },
);

export const selectHiddenIssueCountForSelectedFloorSpace = createSelector(
  [selectIssuesWithAllEnrichedData, selectFilteredIssuesOnBoard, selectSelectedFloorSpace, selectSelectedFloor],
  (allIssues, visibleIssues, selectedFloorSpace, selectedFloor) => {
    const roomNumber = selectedFloorSpace?.name ?? "";

    if (roomNumber.trim() === "") {
      return 0;
    }

    const allIssueIdsForSpace = new Set(
      allIssues
        .filter((issue) => isIssueConnectedToRoomOnFloor(issue, roomNumber, selectedFloor))
        .map((issue) => issue._id),
    );

    const visibleIssueIdsForSpace = new Set(
      visibleIssues
        .filter((issue) => isIssueConnectedToRoomOnFloor(issue, roomNumber, selectedFloor))
        .map((issue) => issue._id),
    );

    let hiddenCount = 0;

    for (const issueId of allIssueIdsForSpace) {
      if (!visibleIssueIdsForSpace.has(issueId)) {
        hiddenCount += 1;
      }
    }

    return hiddenCount;
  },
);

export const selectHttpMcbimFurnitureAndWallsOnSelectedFloor = createSelector(
  [(state: IRootState) => state, selectMcbimSpacesOnSelectedFloorArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimFurnitureAndWallsOnFloor.select(args)(state);
    return q.data ?? [];
  },
);

const FURNITURE_AND_WALL_STYLE: Record<
  | "IfcFurniture"
  | "IfcFurnishingElement"
  | "IfcCurtainWall"
  | "IfcWall"
  | "IfcWallElementedCase"
  | "IfcWallStandardCase"
  | "IfcWindow",
  { fillColor: string; strokeColor?: string; strokeWidth?: string }
> = {
  IfcFurniture: { fillColor: "#E6E1D6", strokeColor: undefined, strokeWidth: undefined },
  IfcFurnishingElement: { fillColor: "#E6E1D6", strokeColor: undefined, strokeWidth: undefined },
  IfcCurtainWall: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
  IfcWall: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
  IfcWallElementedCase: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
  IfcWallStandardCase: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
  IfcWindow: { fillColor: "#B8E0F5", strokeColor: "#5BA6C9", strokeWidth: "1" },
};

const bboxToPolygon = (bbox?: number[][]): number[][] | null => {
  if (!Array.isArray(bbox) || bbox.length < 2) return null;

  if (bbox.length >= 4) {
    const points = bbox
      .filter((point) => Array.isArray(point) && point.length >= 2)
      .map((point) => [point[0], point[1]]);
    return points.length >= 3 ? points : null;
  }

  const [minPoint, maxPoint] = bbox;
  if (!Array.isArray(minPoint) || !Array.isArray(maxPoint) || minPoint.length < 2 || maxPoint.length < 2) return null;

  const [minX, minY] = minPoint;
  const [maxX, maxY] = maxPoint;

  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
};

export const selectUniqueMcbimFloors = createSelector([selectHttpMcbimFloors], (floors) =>
  floors.map((floor) => floor.name),
);

export const selectPixPolygonsForSelectedFloor = createSelector(
  [
    selectHttpMcbimSpacesOnSelectedFloor,
    selectFilteredIssuesOnBoard,
    selectSelectedFloor,
    selectSelectedFloorSpaceId,
    selectSelectedFloorSpaceIds,
    selectRoomSearchText,
  ],
  (spaces, issues, selectedFloor, selectedFloorSpaceId, selectedFloorSpaceIds, roomSearchText): IPixPolygon[] => {
    const issueCountByRoomNumber = getIssueCountByRoomNumberForFloor(issues, selectedFloor);

    return buildSpacePolygons({
      spaces,
      issueCountByRoomNumber,
      selectedFloorSpaceId,
      selectedFloorSpaceIds,
      roomSearchText,
    });
  },
);

export const selectPixPathsForSelectedFloorCeilingGrids = createSelector(
  [selectHttpMcbimSpacesOnSelectedFloor],
  (spaces): IPixPath[] => buildCeilingGridPaths(spaces),
);

export const selectPixPolygonsForSelectedFloorFurnitureAndWalls = createSelector(
  [selectHttpMcbimFurnitureAndWallsOnSelectedFloor],
  (data): IPixPolygon[] => {
    const polygons: IPixPolygon[] = [];

    for (const item of data) {
      const points = bboxToPolygon(item.bbox);
      if (!points) continue;

      const style = FURNITURE_AND_WALL_STYLE[item.instanceClass];

      polygons.push({
        id: `fw-${item._id}`,
        entityId: item._id,
        points,
        fillColor: style.fillColor,
        strokeColor: style.strokeColor,
        strokeWidth: style.strokeWidth,
        text: {
          show: false,
          values: [],
          color: "#1f2a2a",
          fontSize: "1",
        },
        tooltip: [item.name, item.longName].filter(Boolean).join(" - ") || undefined,
        isMoveable: false,
        isDragSelectable: false,
        opacity: 1,
      });
    }

    return polygons;
  },
);

export const selectAllPixPolygonsForSelectedFloor = createSelector(
  [selectPixPolygonsForSelectedFloorFurnitureAndWalls, selectPixPolygonsForSelectedFloor],
  (furnitureAndWallPolygons, spacePolygons): IPixPolygon[] => {
    const furniturePolygons = furnitureAndWallPolygons.filter((polygon) => polygon.fillColor === "#E6E1D6");
    const nonFurniturePolygons = furnitureAndWallPolygons.filter((polygon) => polygon.fillColor !== "#E6E1D6");

    return [...nonFurniturePolygons, ...spacePolygons, ...furniturePolygons];
  },
);

export const selectPixPolygonsForSelectedFloorSpaceItems = createSelector(
  [selectVisibleMcbimItemsInSelectedSpace],
  (items): IPixPolygon[] => buildMcbimItemPolygons(items),
);

export const selectViewerPixPolygonsForSelectedFloor = createSelector(
  [selectAllPixPolygonsForSelectedFloor, selectPixPolygonsForSelectedFloorSpaceItems],
  (floorPolygons, selectedSpaceItemPolygons): IPixPolygon[] => [...floorPolygons, ...selectedSpaceItemPolygons],
);

export const selectPixPolygonsForModalRoom = createSelector([selectHttpMcbimSpaceInModal], (space): IPixPolygon[] => {
  if (space?.polygon == null || space.polygon.length <= 2) {
    return [];
  }

  return [
    buildSpacePolygon({
      space: {
        _id: space._id,
        name: space.name,
        longName: space.longName,
        polygon: space.polygon,
      },
      issueCount: 0,
      selectedFloorSpaceId: space._id,
      selectedFloorSpaceIds: [],
      roomQuery: "",
    }),
  ];
});

export const selectPixPolygonsForModalRoomItems = createSelector(
  [selectVisibleMcbimItemsInModalRoom],
  (items): IPixPolygon[] => buildMcbimItemPolygons(items),
);

export const selectViewerPixPolygonsForModalRoom = createSelector(
  [selectPixPolygonsForModalRoom, selectPixPolygonsForModalRoomItems],
  (spacePolygons, itemPolygons): IPixPolygon[] => [...spacePolygons, ...itemPolygons],
);

export const selectPixPathsForModalRoomCeilingGrids = createSelector(
  [selectHttpMcbimSpaceInModal],
  (space): IPixPath[] => {
    if (space == null) {
      return [];
    }

    return buildCeilingGridPaths([
      {
        _id: space._id,
        ceilingGrids: space.ceilingGrids,
      },
    ]);
  },
);

export const selectMcbimFloorItems = createSelector(
  [selectHttpMcbimFloors, selectFilteredIssuesOnBoard],
  (floors, issues) => {
    const issueCountByFloor = new Map<string, number>();

    for (const issue of issues) {
      const floorsForIssue = new Set(
        (issue.connectedBimSpaces ?? [])
          .map((space) => space.floor?.trim())
          .filter((floor): floor is string => Boolean(floor)),
      );

      for (const floor of floorsForIssue) {
        issueCountByFloor.set(floor, (issueCountByFloor.get(floor) ?? 0) + 1);
      }
    }

    return floors.map((floor) => ({
      floor: floor.name,
      globalId: floor.globalId,
      issueCount: issueCountByFloor.get(floor.name) ?? 0,
    }));
  },
);

export const selectMcbimFloorsHaveIssues = createSelector([selectMcbimFloorItems], (floors) =>
  floors.some((floor) => floor.issueCount > 0),
);

export const selectFilteredMcbimFloors = createSelector(
  [selectMcbimFloorItems, selectFloorSearchText, selectShowOnlyFloorsWithIssues],
  (floors, floorSearchText, showOnlyFloorsWithIssues) => {
    const query = floorSearchText.trim().toLowerCase();
    const visibleFloors = showOnlyFloorsWithIssues ? floors.filter((item) => item.issueCount > 0) : floors;

    if (query === "") {
      return visibleFloors;
    }

    return visibleFloors.filter((item) => item.floor.toLowerCase().includes(query));
  },
);
